import type { ReactElement } from 'react';
import type { Proposal } from '@shared/types';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import { useBindings } from '../../state/BindingsContext';
import {
  allocatedTotal,
  defaultTacticRole,
  deliveryKind,
  momentumJourneyStage,
  pricedLine,
  productLabel,
  resolvedMomentumStage,
  tacticFor,
  type MomentumJourneyStage,
} from '../../lib/plan';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { MOMENTUM_STAGES, tacticsByStage } from './Motifs';
import { Frame, HeadRow, NumberedLabel, SubCard } from './primitives';

/**
 * Same five-stage copy momentum_v1's `CustomerJourneyPage` hardcodes
 * (design.md V6: "Original short copy, not lifted from the deck"; the
 * per-stage `dark` flag is the same "cards 1 (Awareness) and 4 (Conversion)
 * render dark" default the brief's `emphasis: true` calls out). Duplicated
 * locally rather than imported - `momentum/CustomerJourneyPage.tsx` doesn't
 * export it and is another stream's file.
 */
const STAGE_COPY: Record<MomentumJourneyStage, { verb: string; signal: string; emphasis: boolean }> = {
  awareness: { verb: 'Create recognition', signal: 'Reach & attention', emphasis: true },
  consideration: { verb: 'Build confidence', signal: 'Engaged visits', emphasis: false },
  intent: { verb: 'Capture demand', signal: 'Qualified actions', emphasis: false },
  conversion: { verb: 'Turn action into value', signal: 'Leads & sales', emphasis: true },
  retention: { verb: 'Keep the relationship', signal: 'Repeat & referral', emphasis: false },
};

/**
 * Journey stage-weight bars (design.md V15 derivations: "Σ monthlyBudget of
 * channels at the stage ÷ media subtotal (via `tacticsByStage`)"). Returns
 * whole-number percents; null when there is no budget denominator at all
 * (an empty plan), which callers treat as 0 rather than crashing on a
 * divide-by-zero.
 *
 * Numerator and denominator BOTH read `mediaPlan.channels` — the deck's
 * default (recommended) plan. They used to disagree: the numerator summed
 * `mediaPlan.channels[].monthlyBudget` while the denominator took the
 * ACTIVE tier's `pricing.subtotalMonthly`, so a proposal sitting on the
 * lean tier divided the recommended plan's dollars by the lean tier's
 * subtotal and rendered "133% of budget" under a bar clamped to 100%.
 * Sharing one source makes the five bars sum to 100% by construction and
 * leaves the slide independent of `budget.activeTierId`, which the deck no
 * longer lets a rep choose.
 *
 * K3e: stage comes from `resolvedMomentumStage` (authored `funnelStage`
 * first, product table as fallback) - the same resolution `tacticsByStage`
 * uses for the card's own tactic list - so a channel's weight bar always
 * lands on the SAME card as its name, even when the agent placed it on an
 * unconventional stage for that product.
 */
export function journeyStageWeights(proposal: Proposal): Record<MomentumJourneyStage, number | null> {
  const denominator = allocatedTotal(proposal);
  const sums: Record<MomentumJourneyStage, number> = {
    awareness: 0,
    consideration: 0,
    intent: 0,
    conversion: 0,
    retention: 0,
  };
  for (const line of proposal.mediaPlan.channels) {
    const kind = deliveryKind(pricedLine(proposal, line.productId));
    const stage = resolvedMomentumStage(proposal, line.productId, kind);
    sums[stage] += line.monthlyBudget;
  }
  const result = {} as Record<MomentumJourneyStage, number | null>;
  for (const stage of MOMENTUM_STAGES) {
    result[stage.key] = denominator > 0 ? Math.round((sums[stage.key] / denominator) * 100) : null;
  }
  return result;
}

function Chevron(): ReactElement {
  return (
    <span className="mv3-chev" aria-hidden="true">
      ›
    </span>
  );
}

/**
 * customer_journey (brief §4 `06 journey`): FULL light, the populated stage cards (2-5) with
 * mint chevrons between. No authored props beyond `rationale` - tactics, signal copy, and stage-weight bars all derive from
 * plan/strategy state, exactly like momentum_v1's version (design.md V15
 * slide map).
 */
export function CustomerJourneyPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const { productName } = useContentLibrary();
  const labelFor = (productId: string) => productName(productId) ?? productLabel(proposal, productId);
  const byStage = tacticsByStage(proposal, labelFor);
  const weights = journeyStageWeights(proposal);
  // A stage with no tactic is not part of this plan's journey: hide it rather
  // than show an empty "—" card at 0% of budget.
  const populated = MOMENTUM_STAGES.filter((stage) => (byStage.get(stage.key) ?? []).length > 0);
  const visibleStages = populated.length > 0 ? populated : MOMENTUM_STAGES;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-journey-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-full-pane">
          <HeadRow title="The omni-channel customer journey" />
          <div className="mv3-journey-row">
            {visibleStages.map((stage, index) => {
              const copy = STAGE_COPY[stage.key];
              const tactics = byStage.get(stage.key) ?? [];
              const pct = weights[stage.key] ?? 0;
              return (
                <div className="mv3-journey-card-wrap" key={stage.key}>
                  <SubCard tone={copy.emphasis ? 'dark' : 'light'} className="mv3-journey-card">
                    <NumberedLabel
                      number={String(index + 1).padStart(2, '0')}
                      label={stage.label}
                      inverse={copy.emphasis}
                    />
                    <p className="mv3-journey-verb">{copy.verb}</p>
                    <div className="mv3-rule" aria-hidden="true" />
                    <span className="mv3-journey-kicker">Tactics</span>
                    <p className="mv3-journey-tactics">{tactics.join(' · ')}</p>
                    <span className="mv3-journey-kicker">Primary signal</span>
                    <p className="mv3-journey-signal">{copy.signal}</p>
                    <div className="mv3-journey-stagebar" aria-hidden="true">
                      <i style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
                    </div>
                    <span className="mv3-journey-pct">{pct}% of budget</span>
                  </SubCard>
                  {index < visibleStages.length - 1 ? <Chevron /> : null}
                </div>
              );
            })}
          </div>
          <div className="mv3-journey-note">
            <EditableText
              value={section.content.rationale}
              field="rationale"
              placeholder="The mix changes by advertiser. The framework stays the same."
              readOnly={readOnly}
              onSave={(next) => actions.saveContent(section, { rationale: next })}
            />
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}

const ARCHITECTURE_STAGE_ORDER = ['awareness', 'consideration', 'intent', 'conversion', 'loyalty'];

function architectureStageRank(stage: string): number {
  const normalized = stage.toLowerCase();
  const rank = ARCHITECTURE_STAGE_ORDER.findIndex((candidate) => normalized.includes(candidate));
  return rank === -1 ? ARCHITECTURE_STAGE_ORDER.length : rank;
}

/**
 * campaign_architecture (design.md V12, no wireframe - "derive from
 * journey"): FULL light, the same stage-card grid language as
 * `CustomerJourneyPage` above, but the cards and their order come from
 * `strategy.tactics` / `mediaPlan.channels` exactly as momentum_v1's
 * `CampaignArchitecturePage` reads them (design.md V15 slide map), with up
 * to 4 authored `strategy.relationships` beneath, connected by mint
 * chevrons instead of the journey's stage-weight bars.
 */
export function CampaignArchitecturePage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const resolve = useBindings();
  const { productName } = useContentLibrary();
  const labelFor = (productId: string) => productName(productId) ?? productLabel(proposal, productId);
  const tactics = proposal.mediaPlan.channels
    .slice(0, 5)
    .map((line, sourceIndex) => {
      const priced = pricedLine(proposal, line.productId);
      const kind = deliveryKind(priced);
      const tactic = tacticFor(proposal, line.productId);
      return {
        id: line.productId,
        name: labelFor(line.productId),
        // `funnelStage`/`role` are agent-authored free text
        // (proposal.strategy.tactics[]) and can carry a live {{binding}} -
        // kept raw here (sorting only needs the semantic stage, not the
        // display text) and resolved where they're actually rendered below.
        stage: tactic?.funnelStage || momentumJourneyStage(line.productId, kind),
        role: tactic?.role || defaultTacticRole(line.productId, kind),
        sourceIndex,
      };
    })
    .sort((left, right) => {
      const stageDelta = architectureStageRank(left.stage) - architectureStageRank(right.stage);
      return stageDelta || left.sourceIndex - right.sourceIndex;
    });
  const relationships = proposal.strategy.relationships.slice(0, 4);

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-architecture-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-full-pane">
          <HeadRow title={section.title} tagline="How the tactics work together" />
          {tactics.length > 0 ? (
            <div className="mv3-journey-row mv3-architecture-row">
              {tactics.map((tactic, index) => (
                <div className="mv3-journey-card-wrap" key={`${tactic.id}-${index}`}>
                  <SubCard tone="light" className="mv3-journey-card mv3-architecture-card">
                    <NumberedLabel number={String(index + 1).padStart(2, '0')} label={resolve(String(tactic.stage))} />
                    <p className="mv3-architecture-name">{tactic.name}</p>
                    <div className="mv3-rule" aria-hidden="true" />
                    <p className="mv3-architecture-role">{resolve(tactic.role)}</p>
                  </SubCard>
                  {index < tactics.length - 1 ? <Chevron /> : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="note">No tactics selected yet.</p>
          )}
          {relationships.length > 0 ? (
            <div className="mv3-architecture-relationships">
              {relationships.map((rel, index) => (
                <div className="mv3-architecture-relationship-row" key={`${rel.from}-${rel.to}-${index}`}>
                  <span className="mv3-architecture-relationship-names">
                    {labelFor(rel.from)} <span className="mv3-chev-inline">→</span> {labelFor(rel.to)}
                  </span>
                  <p>{resolve(rel.relationship)}</p>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </Frame>
    </PageShell>
  );
}
